import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, ArrowLeft, Shield, Plane, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ResetPassword — shared between `admin` and `agent`. Theme-aware.
 */
const ResetPassword = ({ userType = 'agent' }) => {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get('token') || '';

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const isAdmin = userType === 'admin';
  const endpoint = isAdmin ? '/api/admin/reset-password' : '/api/agent/reset-password';
  const loginPath = isAdmin ? '/admin/login' : '/agent/login';
  const forgotPath = isAdmin ? '/admin/forgot-password' : '/agent/forgot-password';
  const roleLabel = isAdmin ? 'Admin' : 'Agent';
  const Icon = isAdmin ? Shield : Plane;

  const cfg = isAdmin
    ? {
        pageWrap: 'min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 flex items-center justify-center p-4 py-12',
        iconWrap: 'w-16 h-16 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4',
        title: 'text-[#0F172A]',
        focus: 'focus:ring-slate-400/30 focus:border-slate-600',
        link: 'text-slate-700 hover:text-slate-900',
        button: 'w-full py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
      }
    : {
        pageWrap: 'min-h-screen travel-bg flex items-center justify-center p-4 py-12',
        iconWrap: 'w-16 h-16 bg-gradient-to-br from-[#1B375B] to-[#659A9B] rounded-2xl flex items-center justify-center mx-auto mb-4',
        title: 'text-[#1B375B]',
        focus: 'focus:ring-[#659A9B]/30 focus:border-[#659A9B]',
        link: 'text-[#659A9B] hover:text-[#1B375B]',
        button: 'w-full py-4 bg-gradient-to-r from-[#1B375B] to-[#335E77] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none',
      };

  const strength = useMemo(() => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
    if (/\d/.test(pw)) s++;
    if (/[^a-zA-Z0-9]/.test(pw)) s++;
    return s;
  }, [pw]);
  const strengthLabel = ['Too short', 'Weak', 'OK', 'Good', 'Strong'][strength];
  const strengthColor = ['bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-lime-500', 'bg-emerald-500'][strength];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pw !== pw2)   { setErr("Passwords don't match."); return; }
    if (!token)       { setErr('Missing reset token. Open the link from your email.'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}${endpoint}`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate(loginPath), 2500);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (e2?.response?.status === 429) {
        setErr('Too many requests. Please wait a minute and try again.');
      } else {
        setErr(typeof detail === 'string' ? detail : 'Password reset failed. Please try again.');
      }
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className={cfg.pageWrap} data-testid={`${userType}-reset-password-missing-token`}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h1 className={`text-2xl font-bold mb-2 ${cfg.title}`}>Invalid reset link</h1>
            <p className="text-sm text-gray-600 mb-6">
              This URL is missing a reset token. Please request a new reset link.
            </p>
            <button onClick={() => navigate(forgotPath)} className={cfg.button}>
              Request a new link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cfg.pageWrap} data-testid={`${userType}-reset-password-page`}>
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate(loginPath)}
          className={`mb-6 flex items-center gap-2 text-gray-600 transition-colors ${isAdmin ? 'hover:text-slate-900' : 'hover:text-[#1B375B]'}`}
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Login</span>
        </button>

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
          <div className="text-center mb-8">
            <div className={cfg.iconWrap}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-3xl font-bold mb-1 ${cfg.title}`}>Create a new password</h1>
            <p className="text-gray-500 text-sm">
              Set a new password for your ACF Tourism {roleLabel.toLowerCase()} account.
            </p>
          </div>

          {done ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex gap-3" data-testid={`${userType}-reset-success`}>
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900">Password updated</div>
                <div className="text-sm text-emerald-800 mt-1">
                  Your password has been changed. Redirecting you to login…
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    autoFocus
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 8 characters"
                    data-testid={`${userType}-reset-new-pw`}
                    className={`w-full pl-12 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 ${cfg.focus} transition-all`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {pw && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(strength + 1) * 20}%` }} />
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">Strength: <b>{strengthLabel}</b></div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    placeholder="Re-type password"
                    data-testid={`${userType}-reset-confirm-pw`}
                    className={`w-full pl-12 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 ${cfg.focus} transition-all`}
                  />
                </div>
              </div>

              {err && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2" data-testid={`${userType}-reset-error`}>
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{err}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid={`${userType}-reset-submit`}
                className={cfg.button}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Updating…
                  </span>
                ) : 'Update password'}
              </button>

              <p className="text-center text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => navigate(loginPath)}
                  className={`font-semibold ${cfg.link} transition-colors`}
                >
                  Back to login
                </button>
              </p>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">Secure password reset with 256-bit encryption</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
